import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';
import { radius } from '@libertify/ui';
import { createPortal } from 'react-dom';
import { Dropdown, type MenuProps, Button } from 'antd';
import VideoPlayer from './VideoPlayer';
import { PdfViewer } from './PdfViewer';
import { ChatBot } from './Chat/ChatBot';
import { MobileDocHeader } from './MobileDocHeader';
import { SuggestionsOverlay } from './Chat/SuggestionsOverlay';
import { SparkleIcon } from './icons';
import { getUrlAccessToken } from '../utils/auth';
import type Player from 'video.js/dist/types/player';
import type { IHighlight } from 'react-pdf-highlighter';
import {
  DEFAULT_PLAYBACK_RATES,
  type MediaProjectInstanceResponse,
  type CallToActions,
  type Cuepoint,
  type VideoJsOptions,
  type ProjectDocument,
} from '../types';

export type MobileScreenSwapProps = {
  // Video
  videoJsOptions: VideoJsOptions;
  onPlayerReady: (player: Player) => void;
  playerRef: React.RefObject<Player>;
  cuepoints: Cuepoint[] | null;
  highlights: IHighlight[] | null;
  sections: unknown;
  callToActions: CallToActions | undefined;
  pipVideoSize: unknown;
  // Document
  showDocumentAnimation: boolean;
  mainDocument: ProjectDocument | undefined;
  pdfViewerRef: React.RefObject<unknown>;
  // Chat
  hasChat: boolean;
  openChat: boolean;
  setOpenChat: Dispatch<SetStateAction<boolean>>;
  documentIds: string[];
  organizationId: string;
  suggestions: string[] | undefined;
  setSuggestions: (s: string[] | undefined) => void;
  firstQuery: string | undefined;
  setFirstQuery: (q: string | undefined) => void;
  // Publication
  publication: MediaProjectInstanceResponse;
  // Mobile play
  setShowMobilePlayButton: Dispatch<SetStateAction<boolean>>;
  // Language
  changeLanguage: (language: string) => void;
  locale: string;
  // Creator mode
  isCreatorMode: boolean;
  creatorToolbar?: React.ReactNode;
  // Share
  onShareClick?: () => void;
  // Slots
  footerSlot?: React.ReactNode;
};

export const MobileScreenSwap = ({
  videoJsOptions,
  onPlayerReady,
  playerRef,
  cuepoints,
  highlights,
  sections,
  callToActions,
  pipVideoSize,
  showDocumentAnimation,
  mainDocument,
  pdfViewerRef,
  hasChat,
  openChat,
  setOpenChat,
  documentIds,
  organizationId,
  suggestions,
  setSuggestions,
  firstQuery,
  setFirstQuery,
  publication,
  setShowMobilePlayButton,
  isCreatorMode,
  creatorToolbar,
  onShareClick,
  footerSlot,
}: MobileScreenSwapProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [screen, setScreen] = useState<'video' | 'doc'>('video');
  const [playing, setPlaying] = useState(false);
  const [chatExpanded, setChatExpandedRaw] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const searchParams =
    globalThis.window === undefined
      ? null
      : new URLSearchParams(globalThis.window.location.search);
  const previewAccessToken = getUrlAccessToken(searchParams);

  // When user expands the chat, also trigger openChat so the WebSocket
  // connects, and pause playback — the expanded chat covers the video.
  const setChatExpanded = useCallback(
    (val: boolean) => {
      setChatExpandedRaw(val);
      if (val) {
        setOpenChat(true);
        try {
          playerRef.current?.pause();
        } catch {
          // Player not ready yet; nothing to pause.
        }
      }
    },
    [setOpenChat, playerRef],
  );

  // PiP controls
  const [pipControlsVisible, setPipControlsVisible] = useState(false);
  const pipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Body scroll lock — store scroll position and restore on unmount
  const scrollYRef = useRef(0);
  useEffect(() => {
    scrollYRef.current = window.scrollY;
    return () => {
      window.scrollTo(0, scrollYRef.current);
    };
  }, []);

  // Track video play state from player. Named handlers + cleanup: the retry
  // loop runs again on every remount, and without `.off` each remount would
  // stack another set of listeners on the same player instance.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setShowMobilePlayButton(true);
    };
    const onRateChange = () => {
      const rate = playerRef.current?.playbackRate();
      if (rate) setPlaybackRate(rate);
    };

    const attach = () => {
      if (cancelled) return;
      if (playerRef.current) {
        try {
          playerRef.current.on('play', onPlay);
          playerRef.current.on('pause', onPause);
          playerRef.current.on('ended', onEnded);
          playerRef.current.on('ratechange', onRateChange);
        } catch {
          // Player not ready yet
        }
      } else {
        retryTimer = setTimeout(attach, 100);
      }
    };
    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        playerRef.current?.off('play', onPlay);
        playerRef.current?.off('pause', onPause);
        playerRef.current?.off('ended', onEnded);
        playerRef.current?.off('ratechange', onRateChange);
      } catch {
        // Player already disposed
      }
    };
  }, [playerRef, setShowMobilePlayButton]);

  // Swipe detection
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      if (!touch || !touchStartRef.current) return;
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      touchStartRef.current = null;
      // Only count horizontal swipes (not vertical scrolling)
      if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (
        screen === 'video' &&
        dx < -60 &&
        showDocumentAnimation &&
        mainDocument
      ) {
        setScreen('doc');
      } else if (screen === 'doc' && dx > 60) {
        setScreen('video');
      }
    },
    [screen, showDocumentAnimation, mainDocument],
  );

  // PiP tap
  const handlePipTap = useCallback(() => {
    if (pipTimerRef.current) clearTimeout(pipTimerRef.current);
    setPipControlsVisible(true);
    pipTimerRef.current = setTimeout(() => setPipControlsVisible(false), 3000);
  }, []);

  const handlePipExpand = useCallback(() => {
    setScreen('video');
    setPipControlsVisible(false);
  }, []);

  const handlePipPlayPause = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused()) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, [playerRef]);

  const handleVideoTap = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused()) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, [playerRef]);

  const handleSelectSpeed = useCallback(
    (rate: number) => {
      playerRef.current?.playbackRate(rate);
      setPlaybackRate(rate);
    },
    [playerRef],
  );

  const hasDocument = showDocumentAnimation && !!mainDocument;
  const isDoc = screen === 'doc';

  const speedItems: NonNullable<MenuProps['items']> =
    DEFAULT_PLAYBACK_RATES.map((rate) => ({
      key: String(rate),
      label: `${rate}×`,
      onClick: () => handleSelectSpeed(rate),
    }));

  return createPortal(
    <Root onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <ScrollLock />
      {/* ═══ Video layer — always mounted, animates between fullscreen and PiP ═══ */}
      <VideoContainer $pip={isDoc} onClick={isDoc ? undefined : handleVideoTap}>
        <VideoPlayer
          options={videoJsOptions}
          onReady={onPlayerReady}
          cuepoints={cuepoints}
          highlights={highlights}
          sections={sections}
          callToActions={callToActions}
          documentIds={documentIds}
          organizationId={organizationId}
          publication={publication}
          pipVideoSize={pipVideoSize}
        />
        {/* PiP touch overlay + controls — captures all taps in PiP mode */}
        {isDoc && (
          <PipTouchOverlay onClick={handlePipTap}>
            <PipControls $visible={pipControlsVisible}>
              <PipControlBtn
                onClick={(e) => {
                  e.stopPropagation();
                  handlePipPlayPause();
                }}
              >
                {playing ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                )}
              </PipControlBtn>
              <PipControlBtn
                onClick={(e) => {
                  e.stopPropagation();
                  handlePipExpand();
                }}
              >
                <svg width="12" height="12" viewBox="0 0 256 256" fill="white">
                  <path d="M216,48V88a8,8,0,0,1-16,0V67.31l-42.34,42.35a8,8,0,0,1-11.32-11.32L188.69,56H168a8,8,0,0,1,0-16h40A8,8,0,0,1,216,48ZM98.34,146.34,56,188.69V168a8,8,0,0,0-16,0v40a8,8,0,0,0,8,8H88a8,8,0,0,0,0-16H67.31l42.35-42.34a8,8,0,0,0-11.32-11.32Z" />
                </svg>
              </PipControlBtn>
            </PipControls>
          </PipTouchOverlay>
        )}
      </VideoContainer>

      {/* ═══ SCREEN A — Play/Pause controls ═══ */}
      {!isDoc && (
        <>
          <CenterPlayBtn $visible={!playing} onClick={handleVideoTap}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </CenterPlayBtn>
          <FloatingPauseBtn $visible={playing} onClick={handleVideoTap}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          </FloatingPauseBtn>
          {!isCreatorMode && (
            <Dropdown
              menu={{ items: speedItems }}
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={(trigger) => trigger}
            >
              <SpeedButton type="text" aria-label={t('playbackSpeed')}>
                {playbackRate}×
              </SpeedButton>
            </Dropdown>
          )}
          <PoweredByWrap>
            <PoweredByLabel>{t('poweredBy')}</PoweredByLabel>
            <PoweredByLabel $bold>{t('libertify')}</PoweredByLabel>
          </PoweredByWrap>
        </>
      )}

      {/* ═══ SCREEN A — footer slot (e.g. Cookie Settings), below the floating chat ═══ */}
      {!isDoc && footerSlot ? footerSlot : null}

      {/* ═══ SCREEN A overlay — suggestions (only when video is fullscreen) ═══ */}
      {!isDoc && hasChat && (
        <SuggestionsOverlay
          suggestions={suggestions}
          setLayout={() => setChatExpanded(true)}
          setNextQuery={setFirstQuery}
          playerRef={playerRef}
        />
      )}

      {/* ═══ SCREEN B — Document ═══ */}
      {hasDocument && (
        <ScreenB $visible={isDoc}>
          {/* Document header — creator toolbar when in creator mode, else viewer header */}
          {creatorToolbar ? (
            <DocHeaderCreatorWrap>{creatorToolbar}</DocHeaderCreatorWrap>
          ) : (
            <MobileDocHeader
              publication={publication}
              onShareClick={onShareClick}
            />
          )}

          {/* Document content */}
          <DocScrollWrapper>
            <PdfViewer
              url={mainDocument.path}
              ref={pdfViewerRef as React.Ref<unknown>}
              highlights={highlights}
              cuepoints={cuepoints}
              isPrimary={true}
              documentPagesCount={mainDocument?.nb_of_pages}
              isShown={isDoc}
            />
            <DocFadeMask />
          </DocScrollWrapper>
        </ScreenB>
      )}

      {/* ═══ Chat — renders ChatBot directly like desktop ═══ */}
      {hasChat && (
        <>
          {/* Collapsed bar */}
          {!chatExpanded && (
            <ChatCollapsedBar
              $hidden={!isDoc && playing}
              $docked={isDoc}
              onClick={() => setChatExpanded(true)}
            >
              <SparkleIcon />
              <ChatPlaceholder>{t('askMeAnything')}</ChatPlaceholder>
              <ChatAskPill>{t('ask')}</ChatAskPill>
            </ChatCollapsedBar>
          )}
          {/* Chat body — always mounted, hidden when collapsed */}
          <ChatPanel
            $hidden={!chatExpanded || (!isDoc && playing)}
            $docked={isDoc}
            className="mobile-chat-float mobile-chat-float--expanded"
          >
            <ChatPanelHeader>
              <SparkleIcon />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#090e19' }}>
                {isDoc ? t('askAboutDocument') : t('askAboutVideo')}
              </span>
              <ChatCloseBtn
                onClick={() => setChatExpanded(false)}
                aria-label="Close"
              >
                &#x2715;
              </ChatCloseBtn>
            </ChatPanelHeader>
            <div
              className="chat-section__body"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ChatBot
                publication={publication}
                open={openChat || chatExpanded}
                documentIds={documentIds}
                organizationId={organizationId ?? ''}
                firstQuery={firstQuery}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                previewAccessToken={previewAccessToken}
              />
            </div>
          </ChatPanel>
        </>
      )}
    </Root>,
    document.body,
  );
};

// ─── Styled Components ─────────────────────────────────────────────────────

/**
 * Locks scrolling on html/body while the mobile fullscreen portal is mounted.
 * Uses !important to override any inline or app-level styles.
 */
const ScrollLock = createGlobalStyle`
  html {
    overflow: hidden !important;
  }
  body {
    overflow: hidden !important;
    overscroll-behavior: none !important;
  }
  /* Hide Intercom widget behind the fullscreen portal */
  .intercom-lightweight-app,
  .intercom-launcher,
  .intercom-app,
  #intercom-container {
    display: none !important;
  }
`;

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Fallback chain: dvh → fill-available → 100% */
  height: 100dvh;
  background: #000;
  overflow: hidden;
  z-index: 1001;
  touch-action: pan-y;
  /* iOS safe areas (notch, home indicator) */
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
    env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
  box-sizing: border-box;
`;

/**
 * The video container animates between full-screen (Screen A)
 * and a small PiP thumbnail (Screen B, top-left corner).
 * The actual <video> element stays mounted — no re-render.
 */
const VideoContainer = styled.div<{ $pip: boolean }>`
  position: absolute;
  z-index: ${(p) => (p.$pip ? 30 : 1)};
  transition:
    top 0.35s ease,
    left 0.35s ease,
    width 0.35s ease,
    height 0.35s ease,
    border-radius 0.35s ease;

  /* Full-screen */
  top: ${(p) => (p.$pip ? '60px' : '0')};
  left: ${(p) => (p.$pip ? '16px' : '0')};
  width: ${(p) => (p.$pip ? '108px' : '100%')};
  height: ${(p) => (p.$pip ? '177px' : '100%')};
  border-radius: ${(p) => (p.$pip ? `${radius.box}px` : '0')};
  overflow: hidden;
  background: #000;

  ${(p) =>
    p.$pip &&
    `
    box-shadow: 0 4px 20px rgba(0,0,0,0.35);
    cursor: pointer;
  `}

  /* Force the StyledVideo ([data-vjs-player]) wrapper to fill this container */
  & > [data-vjs-player] {
    width: 100% !important;
    height: 100% !important;
  }
  /* Force .video-player to fill, overriding any inline top/left styles */
  .video-player {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transform: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  /* Also flatten video-wrapper layers if present */
  .video-wrapper-container,
  .video-wrapper,
  .iphone-x {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: none !important;
    border-radius: 0 !important;
    outline: none !important;
    aspect-ratio: unset !important;
  }
  .iphone-x > i,
  .iphone-x > b,
  .iphone-x > s,
  .iphone-x > span,
  .iphone-x::before,
  .iphone-x::after {
    display: none !important;
  }
  /* Force video.js to fill */
  .video-js {
    width: 100% !important;
    height: 100% !important;
    padding-top: 0 !important;
    border-radius: 0 !important;
  }
  .video-js video {
    object-fit: ${(p) => (p.$pip ? 'cover' : 'contain')};
  }
  /* Disable movingDiv constraints (cuepoint animation mode) */
  .movingDiv {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transform: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .movingDiv .video-js {
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
    border-radius: 0 !important;
  }
  .header-logo {
    display: none !important;
  }
  /* Hide controls in PiP mode */
  ${(p) =>
    p.$pip &&
    `
    .vjs-control-bar { display: none !important; }
    .vjs-big-play-button { display: none !important; }
  `}
`;

const ScreenB = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  overflow: hidden;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transform: ${(p) => (p.$visible ? 'translateX(0)' : 'translateX(100%)')};
`;

const DocHeaderCreatorWrap = styled.div`
  flex-shrink: 0;
  z-index: 10;
`;

// ─── Video Play/Pause Controls (Screen A) ────────────────────────────────

const CenterPlayBtn = styled.button<{ $visible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  width: 72px;
  height: 72px;
  border-radius: ${radius.circle};
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s,
    transform 0.15s,
    opacity 0.3s;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};

  &:active {
    background: rgba(255, 255, 255, 0.25);
    transform: translate(-50%, -50%) scale(0.94);
  }
`;

const FloatingPauseBtn = styled.button<{ $visible: boolean }>`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 10;
  width: 48px;
  height: 48px;
  border-radius: ${radius.circle};
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s,
    opacity 0.3s;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};

  &:active {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const SpeedButton = styled(Button)`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.15) !important;

  &:active {
    background: rgba(255, 255, 255, 0.25) !important;
  }
`;

// ─── Powered By (Screen A footer) ────────────────────────────────────────

const PoweredByWrap = styled.div`
  position: absolute;
  bottom: 116px;
  left: 0;
  right: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
`;

const PoweredByLabel = styled.span<{ $bold?: boolean }>`
  font-size: 12px;
  color: ${(p) =>
    p.$bold ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)'};
  font-weight: ${(p) => (p.$bold ? 700 : 400)};
`;

// ─── PiP Controls (overlay on VideoContainer when in PiP mode) ───────────

const PipTouchOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
`;

const PipControls = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: opacity 0.2s;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
`;

const PipControlBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: ${radius.circle};
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:active {
    background: rgba(255, 255, 255, 0.35);
  }
`;

// ─── Document Content ─────────────────────────────────────────────────────

const DocScrollWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  min-height: 0;
`;

const DocFadeMask = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(180deg, transparent, #f9fafb);
  pointer-events: none;
  margin-top: -80px;
`;

// ─── Chat (inline, no wrapper component) ──────────────────────────────────

const ChatCollapsedBar = styled.div<{
  $hidden: boolean;
  $docked: boolean;
}>`
  position: absolute;
  bottom: ${(p) => (p.$docked ? '0' : '48px')};
  left: ${(p) => (p.$docked ? '0' : '24px')};
  right: ${(p) => (p.$docked ? '0' : '24px')};
  z-index: 20;
  background: white;
  border-radius: ${(p) => (p.$docked ? '0' : `${radius.box}px`)};
  box-shadow: ${(p) => (p.$docked ? 'none' : '0 8px 32px rgba(0,0,0,0.22)')};
  border-top: ${(p) => (p.$docked ? '1px solid #f3f4f6' : 'none')};
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${(p) => (p.$docked ? '16px 24px 20px' : '12px 16px')};
  cursor: pointer;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
  transition: opacity 0.3s ease-out;
  box-sizing: border-box;
`;

const ChatPlaceholder = styled.span`
  flex: 1;
  font-size: 14px;
  color: #9ca3af;
`;

const ChatAskPill = styled.span`
  background: #14b8a6;
  color: white;
  border-radius: ${radius.pill}px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ChatPanel = styled.div<{ $hidden: boolean; $docked: boolean }>`
  position: absolute;
  bottom: ${(p) => (p.$docked ? '0' : '48px')};
  left: ${(p) => (p.$docked ? '0' : '24px')};
  right: ${(p) => (p.$docked ? '0' : '24px')};
  height: 280px;
  max-height: 60dvh;
  z-index: 20;
  background: white;
  border-radius: ${(p) => (p.$docked ? '0' : `${radius.box}px`)};
  box-shadow: ${(p) => (p.$docked ? 'none' : '0 8px 32px rgba(0,0,0,0.22)')};
  border-top: ${(p) => (p.$docked ? '1px solid #f3f4f6' : 'none')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
  transition: opacity 0.3s ease-out;
`;

const ChatPanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #f3f4f6;
`;

const ChatCloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;
  border-radius: 6px;
  margin-left: auto;
  font-size: 14px;

  &:active {
    background: #f3f4f6;
    color: #374151;
  }
`;
